import React, { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { contractsAPI } from '@/lib/api';
import { formatCurrency, formatDate, getDaysUntil, getCompetitivenessColor, getStatusColor, getCustomerStatusColor } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Filter, ArrowUpDown, Plus, RefreshCw, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function ContractsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [expiringDaysFilter, setExpiringDaysFilter] = useState('ALL');
  const [sorting, setSorting] = useState([]);
  const [page, setPage] = useState(1);
  const limit = 20;

  // Initialize filters from URL params
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const expiringParam = searchParams.get('expiring_days');
    
    if (statusParam) {
      setStatusFilter(statusParam);
    }
    if (expiringParam) {
      setExpiringDaysFilter(expiringParam);
    }
  }, [searchParams]);

  // Update URL when filters change
  const updateFilters = (newStatus, newExpiringDays) => {
    const params = new URLSearchParams();
    if (newStatus && newStatus !== 'ALL') {
      params.set('status', newStatus);
    }
    if (newExpiringDays && newExpiringDays !== 'ALL') {
      params.set('expiring_days', newExpiringDays);
    }
    setSearchParams(params);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPage(1);
    updateFilters(value, expiringDaysFilter);
  };

  const handleExpiringDaysChange = (value) => {
    setExpiringDaysFilter(value);
    setPage(1);
    updateFilters(statusFilter, value);
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['contracts', page, statusFilter, expiringDaysFilter],
    queryFn: () =>
      contractsAPI
        .getAll({
          page,
          limit,
          status: statusFilter !== 'ALL' ? statusFilter : undefined,
          expiring_days: expiringDaysFilter !== 'ALL' ? parseInt(expiringDaysFilter) : undefined,
        })
        .then((res) => res.data),
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: 'contract_number',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="hover:bg-transparent pl-0"
          >
            Contract #
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div className="font-medium text-[#09409C]">{row.original.contract_number}</div>
        ),
      },
      {
        accessorKey: 'account_name',
        header: 'Account',
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.account_name}</div>
            <div className="flex items-center gap-2 mt-1">
              <Badge
                variant="outline"
                className={`text-xs ${
                  getCustomerStatusColor(row.original.customer_status) === 'purple'
                    ? 'border-purple-300 text-purple-700 bg-purple-50'
                    : getCustomerStatusColor(row.original.customer_status) === 'blue'
                    ? 'border-blue-300 text-blue-700 bg-blue-50'
                    : 'border-gray-300 text-gray-700 bg-gray-50'
                }`}
              >
                {row.original.customer_status}
              </Badge>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const color = getStatusColor(row.original.status);
          return (
            <Badge
              className={`${
                color === 'green'
                  ? 'bg-green-100 text-green-800'
                  : color === 'yellow'
                  ? 'bg-yellow-100 text-yellow-800'
                  : color === 'red'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {row.original.status.replace('_', ' ')}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'annual_value',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="hover:bg-transparent pl-0"
          >
            Annual Value
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <div className="font-semibold">{formatCurrency(row.original.annual_value)}</div>,
      },
      {
        accessorKey: 'end_date',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="hover:bg-transparent pl-0"
          >
            End Date
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div>
            <div>{formatDate(row.original.end_date)}</div>
            <div className="text-xs text-muted-foreground">
              {row.original.days_to_renewal > 0
                ? `${row.original.days_to_renewal} days`
                : `${Math.abs(row.original.days_to_renewal)} days ago`}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'competitiveness_score',
        header: 'Competitiveness',
        cell: ({ row }) => {
          const score = row.original.competitiveness_score;
          if (!score) return <span className="text-gray-400">N/A</span>;
          const color = getCompetitivenessColor(score);
          return (
            <Badge
              className={`${
                color === 'green'
                  ? 'bg-green-100 text-green-800'
                  : color === 'blue'
                  ? 'bg-blue-100 text-blue-800'
                  : color === 'yellow'
                  ? 'bg-yellow-100 text-yellow-800'
                  : color === 'orange'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {score}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'assigned_user_name',
        header: 'Assigned To',
        cell: ({ row }) => (
          <div className="text-sm text-gray-700">{row.original.assigned_user_name || 'Unassigned'}</div>
        ),
      },
    ],
    []
  );

  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    if (!search) return data.data;
    return data.data.filter(
      (contract) =>
        contract.contract_number.toLowerCase().includes(search.toLowerCase()) ||
        contract.account_name.toLowerCase().includes(search.toLowerCase())
    );
  }, [data, search]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const handleRefresh = () => {
    refetch();
    toast.success('Contracts refreshed');
  };

  return (
    <div className="p-8 space-y-6" data-testid="contracts-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="text-gradient-primary">Contracts</span>
          </h1>
          <p className="text-muted-foreground mt-2">Manage and monitor all commercial contracts</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleRefresh} data-testid="refresh-button">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            className="bg-gradient-to-r from-[#09409C] to-[#146CBB] hover:from-[#072e70] hover:to-[#105a99]"
            onClick={() => navigate('/contracts/new')}
            data-testid="add-contract-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Contract
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by contract number or account name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={expiringDaysFilter} onValueChange={handleExpiringDaysChange}>
                <SelectTrigger className="w-[160px]" data-testid="expiring-days-filter">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Dates</SelectItem>
                  <SelectItem value="30">Expiring in 30 Days</SelectItem>
                  <SelectItem value="60">Expiring in 60 Days</SelectItem>
                  <SelectItem value="90">Expiring in 90 Days</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Status</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="PENDING_RENEWAL">Pending Renewal</SelectItem>
                  <SelectItem value="EXPIRED">Expired</SelectItem>
                  <SelectItem value="TERMINATED">Terminated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-64 text-center">
                      <div className="flex items-center justify-center">
                        <RefreshCw className="w-6 h-6 animate-spin text-[#09409C]" />
                        <span className="ml-2">Loading contracts...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      onClick={() => navigate(`/contracts/${row.original.id}`)}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      data-testid={`contract-row-${row.original.id}`}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-64 text-center text-muted-foreground">
                      No contracts found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {data && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-muted-foreground">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, data.meta.total)} of {data.meta.total} contracts
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="prev-page-button"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= data.meta.pages}
                  data-testid="next-page-button"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
